import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs"
import { Suspense, useMemo } from "react"
import { Book, MoreHorizontal } from "react-feather"
import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { z as zod } from "zod"
import { type OwnerModels, ownerApi, ownerProperties, ownerSchemas } from "@/api/client/owner"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { PaginationSection } from "@/components/ui/pagination-section"
import { Select } from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { openInNewTab } from "@/lib/navigation"

const echoesOwnerFilterValues = ["all", "true", "false"] as const
type EchoesOwnerFilterValue = (typeof echoesOwnerFilterValues)[number]

const echoesOwnerFilterOptions = [
  { value: "all", label: "すべて" },
  { value: "true", label: "あり" },
  { value: "false", label: "なし" },
]

function normalizeEchoesOwnerFilter(value: string | null | undefined): EchoesOwnerFilterValue {
  return value === "true" || value === "false" ? value : "all"
}

// 検索フォーム用のスキーマを拡張
const searchFormSchema = ownerSchemas.ownerListQueryParams
  .extend({
    id: zod.string().optional(),
    nameLike: zod.string().optional(),
    nameKanaLike: zod.string().optional(),
    emailLike: zod.string().optional(),
    phoneNumberLike: zod.string().optional(),
    isEchoesOwner: zod.enum(echoesOwnerFilterValues).optional(),
  })
  .omit({ page: true, limit: true })

type SearchFormValues = zod.infer<typeof searchFormSchema>

// nuqsのパーサー定義
const ownnerPageParsers = {
  page: parseAsInteger.withDefault(1),
  id: parseAsString,
  nameLike: parseAsString,
  nameKanaLike: parseAsString,
  emailLike: parseAsString,
  phoneNumberLike: parseAsString,
  isEchoesOwner: parseAsString,
}

export const OwnerPage = () => {
  const itemsPerPage = 20
  const queryClient = useQueryClient()

  // URLクエリパラメーター管理
  const [queryParams, setQueryParams] = useQueryStates(ownnerPageParsers)

  // フォームの値をクエリから復元
  const searchParams = useMemo(() => {
    if (!queryParams) return null
    const hasSearchParams = Object.entries(queryParams)
      .filter(([key]) => key !== "page")
      .some(
        ([_, value]) =>
          value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : true)
      )

    if (!hasSearchParams) return null

    return {
      id: queryParams.id || "",
      nameLike: queryParams.nameLike || "",
      nameKanaLike: queryParams.nameKanaLike || "",
      emailLike: queryParams.emailLike || "",
      phoneNumberLike: queryParams.phoneNumberLike || "",
      isEchoesOwner: normalizeEchoesOwnerFilter(queryParams.isEchoesOwner),
    } as SearchFormValues
  }, [queryParams])

  const defaultSearchFormValues: SearchFormValues = {
    id: queryParams.id || "",
    nameLike: queryParams.nameLike || "",
    nameKanaLike: queryParams.nameKanaLike || "",
    emailLike: queryParams.emailLike || "",
    phoneNumberLike: queryParams.phoneNumberLike || "",
    isEchoesOwner: normalizeEchoesOwnerFilter(queryParams.isEchoesOwner),
  }

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: defaultSearchFormValues,
  })

  const onSubmit = (formData: SearchFormValues) => {
    // URLパラメーターを更新
    setQueryParams({
      page: 1, // 検索時はページを1にリセット
      id: formData.id || null,
      nameLike: formData.nameLike || null,
      nameKanaLike: formData.nameKanaLike || null,
      emailLike: formData.emailLike || null,
      phoneNumberLike: formData.phoneNumberLike || null,
      isEchoesOwner: formData.isEchoesOwner === "all" ? null : formData.isEchoesOwner || null,
    })
    queryClient.invalidateQueries({ queryKey: ["/owners"] })
  }

  const onReset = () => {
    form.reset({
      id: "",
      nameLike: "",
      nameKanaLike: "",
      emailLike: "",
      phoneNumberLike: "",
      isEchoesOwner: "all",
    })

    // URLパラメーターをクリア
    setQueryParams({
      page: null,
      id: null,
      nameLike: null,
      nameKanaLike: null,
      emailLike: null,
      phoneNumberLike: null,
      isEchoesOwner: null,
    })
  }

  const handlePageChange = (page: number) => {
    setQueryParams({ page })
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">オーナー一覧</h1>

      <div className="">
        <div className="p-4 border-b bg-white rounded-lg shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>オーナーID</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nameLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>オーナー名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nameKanaLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>オーナー名（カナ）</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="emailLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>メールアドレス</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="phoneNumberLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>電話番号</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isEchoesOwner"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>Echoes連携</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          options={echoesOwnerFilterOptions}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" variant="default" size={"sm"}>
                  検索
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className={"font-bold border-primary"}
                  size={"sm"}
                  onClick={onReset}
                >
                  検索をリセット
                </Button>
              </div>
            </form>
          </Form>
        </div>

        <ReactErrorBoundary>
          <Suspense fallback={<OwnerPageFallback />}>
            <OwnerPageContent
              searchParams={searchParams}
              currentPage={queryParams.page}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
            />
          </Suspense>
        </ReactErrorBoundary>
      </div>
    </div>
  )
}

interface OwnerPageContentProps {
  searchParams: SearchFormValues | null
  currentPage: number
  onPageChange: (page: number) => void
  itemsPerPage: number
}

function OwnerPageContent({
  searchParams,
  currentPage,
  onPageChange,
  itemsPerPage,
}: OwnerPageContentProps) {
  const apiParams = useMemo(() => {
    const params: OwnerModels.OwnerListParams = {
      page: currentPage,
      limit: itemsPerPage,
    }

    type SearchableKeys = keyof OwnerModels.OwnerListParams
    const keys: readonly SearchableKeys[] = [
      "id",
      "nameLike",
      "nameKanaLike",
      "emailLike",
      "phoneNumberLike",
    ] as const

    keys.forEach((key) => {
      if (searchParams?.[key as keyof SearchFormValues]) {
        params[key as keyof OwnerModels.OwnerListParams] = searchParams[
          key as keyof SearchFormValues
          // biome-ignore lint/suspicious/noExplicitAny: 暫定対応
        ] as any
      }
    })
    if (searchParams?.isEchoesOwner === "true") {
      params.isEchoesOwner = ownerProperties.OwnerListIsEchoesOwner.true
    } else if (searchParams?.isEchoesOwner === "false") {
      params.isEchoesOwner = ownerProperties.OwnerListIsEchoesOwner.false
    }
    params.withProperties = ownerProperties.OwnerListWithProperties.true
    return params
  }, [searchParams, currentPage, itemsPerPage])

  const handleDetail = (id: string) => {
    openInNewTab(`/owners/${id}`)
  }

  const { data } = ownerApi.useOwnerListSuspense(apiParams)

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  return (
    <div className="mt-4">
      <div className="flex items-end justify-between mb-4">
        <div className="text-sm text-slate-400">{data.total}件のオーナーが見つかりました</div>
        {/* 追加導線は一旦非表示 */}
        {/* <div>
          <Link to="/owners/add">
            <Button size={"sm"}>
              <Plus className="mr-2" size={16} />
              新規オーナーを追加
            </Button>
          </Link>
        </div> */}
      </div>

      <div className="bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap font-bold">オーナーID</TableHead>
              <TableHead className="whitespace-nowrap font-bold min-w-[250px]">
                オーナー名
              </TableHead>
              <TableHead className="whitespace-nowrap font-bold">オーナー名（カナ）</TableHead>
              <TableHead className="whitespace-nowrap font-bold">メールアドレス</TableHead>
              <TableHead className="whitespace-nowrap font-bold">メール認証</TableHead>
              <TableHead className="whitespace-nowrap font-bold">電話番号</TableHead>
              <TableHead className="whitespace-nowrap font-bold">保有物件数</TableHead>
              <TableHead className="whitespace-nowrap font-bold">Echoes連携</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.owners.map((owner) => (
              <TableRow key={owner.id}>
                <TableCell>{owner.id}</TableCell>
                <TableCell>
                  <Link
                    className="text-blue-600 cursor-pointer hover:underline"
                    to={`/owners/${owner.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {owner.name}
                  </Link>
                </TableCell>
                <TableCell>
                  {[owner.lastNameKana, owner.firstNameKana].filter(Boolean).join("") || "-"}
                </TableCell>
                <TableCell>{owner.email}</TableCell>
                <TableCell>{owner.emailVerified ? "認証済み" : "未認証"}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span>{owner.phoneNumber}</span>
                    {owner.emergencyPhoneNumber && <span>{owner.emergencyPhoneNumber}</span>}
                  </div>
                </TableCell>
                <TableCell>{owner.properties?.length}</TableCell>
                <TableCell>{owner.echoesOwnerId ?? "-"}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[200px]">
                      <DropdownMenuItem onClick={() => handleDetail(owner.id)}>
                        <Book className="text-slate-600 mr-2" size={16} />
                        詳細
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {data.total === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  オーナーが見つかりませんでした
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationSection
        currentPage={currentPage}
        totalCount={data.total}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

function OwnerPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className={"text-slate-600 size-10 transition-opacity"} />
    </div>
  )
}
